<template>
  <section class="container-fluid mt-5 py-5 warranty-claims-section">
    <div class="container mt-5 px-3">
      <!-- Header Section -->
      <div class="row align-items-center mb-5">
        <div class="col-md-6 mb-3 mb-md-0">
          <div class="d-flex align-items-center gap-3">
            <h2 class="mb-1 d-flex align-items-center text-light fs-1">
              Reclamos de Garantía<span class="text-primary">.</span>
            </h2>
            <button 
              class="btn btn-outline-primary"
              @click="forceRefresh"
              :disabled="isLoading"
            >
              <i class="fas fa-sync-alt me-2"></i>
              {{ isLoading ? 'Actualizando...' : 'Actualizar' }}
            </button>
          </div>
          <p class="text-muted mb-0 fs-5">
            <i class="fas fa-shield-alt me-1 text-primary"></i>
            {{ totalClaims }} reclamos registrados
          </p>
          <!-- Filters -->
          <div class="col-md-10 my-3 mb-md-0">
            <div class="input-group">
              <span class="input-group-text bg-white border-end-0 text-muted">
                <i class="fas fa-search"></i>
              </span>
              <input
                type="text"
                class="form-control border-start-0 ps-0 text-black"
                v-model="searchQuery"
                placeholder="Buscar por nombre, email, número de pedido o producto..."
                @input="debounceSearch"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="isLoading" class="row g-4">
        <div v-for="n in 8" :key="n" class="col-lg-4">
          <div class="skeleton-loader">
            <div class="skeleton-text w-50"></div>
            <div class="skeleton-text w-75"></div>
            <div class="skeleton-text w-75"></div>
          </div>
        </div>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="alert alert-danger" role="alert">
        <i class="fas fa-exclamation-triangle me-2"></i>
        {{ error }}
        <button @click="fetchClaims" class="btn btn-outline-danger btn-sm ms-3">
          <i class="fas fa-sync-alt me-1"></i>Reintentar
        </button>
      </div>

      <!-- Claims List -->
      <div v-else class="row g-4">
        <div v-for="claim in filteredClaims" :key="claim.id" class="col-md-6 col-lg-4">
          <div class="card bg-dark text-light">
            <div class="card-body d-flex flex-column">
              <!-- Claim Header -->
              <div class="d-flex justify-content-between align-items-center mb-3">
                <div>
                  <span class="claim-id text-muted me-2">#{{ claim.id }}</span>
                  <span 
                    class="badge"
                    :class="{
                      'bg-warning': claim.status === 'pending',
                      'bg-info': claim.status === 'reviewing',
                      'bg-success': claim.status === 'approved',
                      'bg-danger': claim.status === 'rejected',
                      'bg-primary': claim.status === 'resolved'
                    }"
                  >
                    <i :class="getStatusIcon(claim.status)" class="me-1"></i>
                    {{ getStatusLabel(claim.status) }}
                  </span>
                </div>
                <small class="text-muted">{{ formatDate(claim.created_at) }}</small>
              </div>

              <!-- Claim Info -->
              <div class="claim-info mb-3">
                <div class="info-row">
                  <i class="fas fa-user text-primary"></i>
                  <span class="fw-lightbold">{{ claim.name }}</span>
                  <div class="ms-auto contact-buttons">
                    <a 
                      :href="`mailto:${claim.email}`"
                      class="btn btn-info text-white btn-sm me-1"
                      title="Enviar email"
                    >
                      <i class="fas fa-envelope"></i>
                    </a>
                    <button 
                      @click="viewUserDetails(claim)"
                      class="btn btn-outline-light btn-sm"
                      title="Ver perfil del usuario"
                    >
                      <i class="fas fa-user-circle"></i>
                    </button>
                  </div>
                </div>
                <div class="info-row">
                  <i class="fas fa-envelope text-primary"></i>
                  <span class="text-muted">{{ claim.email }}</span>
                </div>
                <div class="info-row">
                  <i class="fas fa-user-tag text-primary"></i>
                  <span v-if="claim.user_id || (claim.order && claim.order.customer_id)" class="badge bg-success">
                    <i class="fas fa-user-check me-1"></i>Usuario registrado
                  </span>
                  <span v-else class="badge bg-warning">
                    <i class="fas fa-user-alt-slash me-1"></i>Usuario no registrado
                  </span>
                </div>
                <div class="info-row">
                  <i class="fas fa-shopping-bag text-primary"></i>
                  <span>Pedido #{{ claim.order_number }}</span>
                </div>
                <div class="info-row">
                  <i class="fas fa-seedling text-primary"></i>
                  <span>{{ claim.product_name }}</span>
                </div>
                <div class="info-row">
                  <i class="far fa-calendar-check text-primary"></i>
                  <span>Compra: {{ formatSimpleDate(claim.purchase_date) }}</span>
                </div>
                <div class="info-row">
                  <i class="fas fa-calendar-alt text-primary"></i>
                  <span>Germinación: {{ formatSimpleDate(claim.germination_date) }}</span>
                </div>
                <div v-if="claim.photo1 || claim.photo2 || claim.photo3" class="info-row">
                  <i class="fas fa-images text-primary"></i>
                  <span>{{ (claim.photo1 ? 1 : 0) + (claim.photo2 ? 1 : 0) + (claim.photo3 ? 1 : 0) }} fotos adjuntas</span>
                </div>
              </div>

              <!-- Claim Actions -->
              <div class="d-flex justify-content-between align-items-end mt-auto gap-2">
                <button class="btn btn-secondary flex-grow-1" @click="viewClaimDetails(claim)">
                  <i class="fas fa-eye me-2"></i>
                  Ver Detalles
                </button>
                <button class="btn btn-primary flex-grow-1" @click="editClaim(claim)">
                  <i class="fas fa-edit me-2"></i>
                  Editar Estado
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-if="!isLoading && filteredClaims.length === 0" class="text-center py-5 text-muted">
        <i class="fas fa-shield-alt fa-3x mb-3"></i>
        <h4>No se encontraron reclamos</h4>
        <p>No hay reclamos de garantía registrados</p>
      </div>
    </div>

    <!-- View Claim Modal -->
    <div class="modal fade mt-4" id="claimDetailsModal" tabindex="-1">
      <div class="modal-dialog modal-lg">
        <div class="modal-content bg-dark text-light">
          <div class="modal-header border-bottom border-secondary">
            <h5 class="modal-title">
              Reclamo #{{ selectedClaim?.id }}
              <span 
                class="badge ms-2"
                :class="getStatusClass(selectedClaim?.status)"
              >
                {{ getStatusLabel(selectedClaim?.status) }}
              </span>
            </h5>
            <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body" v-if="selectedClaim">
            <!-- Customer Information -->
            <div class="section-card mb-4">
              <h6 class="section-title">
                <i class="fas fa-user me-2"></i>Información del Cliente
              </h6>
              <div class="info-grid">
                <div class="info-item">
                  <span class="label">Nombre:</span>
                  <span>{{ selectedClaim.name }}</span>
                </div>
                <div class="info-item">
                  <span class="label">Email:</span>
                  <div class="d-flex align-items-center">
                    <a :href="`mailto:${selectedClaim.email}`" class="text-primary me-2">
                      {{ selectedClaim.email }}
                    </a>
                  </div>
                </div>
                <div class="info-item">
                  <span class="label">Estado usuario:</span>
                  <div>
                    <span v-if="selectedClaim.user_id || (selectedClaim.order && selectedClaim.order.customer_id)" 
                      class="badge bg-success">
                      <i class="fas fa-user-check me-1"></i>Usuario registrado
                    </span>
                    <span v-else class="badge bg-warning">
                      <i class="fas fa-user-alt-slash me-1"></i>Usuario no registrado
                    </span>
                    
                    <button 
                      @click="viewUserDetails(selectedClaim)" 
                      class="btn btn-outline-primary btn-sm ms-2" 
                      title="Ver perfil del usuario"
                    >
                      <i class="fas fa-user-circle me-1"></i> Ver perfil completo
                    </button>
                  </div>
                </div>
                <div class="info-item" v-if="selectedClaim.user_phone">
                  <span class="label">Teléfono:</span>
                  <a :href="`tel:${selectedClaim.user_phone}`" class="text-primary">
                    {{ selectedClaim.user_phone }}
                  </a>
                </div>
                <div class="info-item" v-if="selectedClaim.user_since">
                  <span class="label">Cliente desde:</span>
                  <span>{{ formatDate(selectedClaim.user_since) }}</span>
                </div>
                <div class="info-item" v-if="selectedClaim.previous_orders_count">
                  <span class="label">Pedidos anteriores:</span>
                  <span>{{ selectedClaim.previous_orders_count }}</span>
                </div>
              </div>
            </div>

            <!-- Claim Details -->
            <div class="section-card mb-4">
              <h6 class="section-title">
                <i class="fas fa-info-circle me-2"></i>Detalles del Reclamo
              </h6>
              <div class="info-grid">
                <div class="info-item">
                  <span class="label">Pedido:</span>
                  <span class="text-primary cursor-pointer" @click="viewOrderDetails(selectedClaim.order_number)">
                    #{{ selectedClaim.order_number }}
                  </span>
                </div>
                <div class="info-item">
                  <span class="label">Producto:</span>
                  <span>{{ selectedClaim.product_name }}</span>
                </div>
                <div class="info-item">
                  <span class="label">Fecha de compra:</span>
                  <span>{{ formatSimpleDate(selectedClaim.purchase_date) }}</span>
                </div>
                <div class="info-item">
                  <span class="label">Fecha de germinación:</span>
                  <span>{{ formatSimpleDate(selectedClaim.germination_date) }}</span>
                </div>
                <div class="info-item">
                  <span class="label">Problema:</span>
                  <p class="mb-0 text-muted">{{ selectedClaim.description }}</p>
                </div>
                <div class="info-item" v-if="selectedClaim.resolution_details">
                  <span class="label">Resolución:</span>
                  <p class="mb-0 text-success">{{ selectedClaim.resolution_details }}</p>
                </div>
                <div class="info-item" v-if="selectedClaim.admin_notes">
                  <span class="label">Notas administrativas:</span>
                  <p class="mb-0 fst-italic text-muted">{{ selectedClaim.admin_notes }}</p>
                </div>
              </div>
            </div>

            <!-- Photos -->
            <div class="section-card">
              <h6 class="section-title">
                <i class="fas fa-images me-2"></i>Fotos
              </h6>
              <div class="photos-grid">
                <div 
                  v-if="selectedClaim.photo1"
                  class="photo-item"
                  @click="openPhotoModal(selectedClaim.photo1)"
                >
                  <img :src="selectedClaim.photo1" alt="Foto 1 - Blister">
                  <span class="photo-label">Blister</span>
                </div>
                <div 
                  v-if="selectedClaim.photo2" 
                  class="photo-item"
                  @click="openPhotoModal(selectedClaim.photo2)"
                >
                  <img :src="selectedClaim.photo2" alt="Foto 2 - Proceso">
                  <span class="photo-label">Proceso</span>
                </div>
                <div 
                  v-if="selectedClaim.photo3" 
                  class="photo-item"
                  @click="openPhotoModal(selectedClaim.photo3)"
                >
                  <img :src="selectedClaim.photo3" alt="Foto 3 - Resultado">
                  <span class="photo-label">Resultado</span>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer border-top border-secondary">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cerrar</button>
            <button type="button" class="btn btn-primary" @click="editClaim(selectedClaim)">
              <i class="fas fa-edit me-2"></i>Editar Estado
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Edit Claim Modal -->
    <div class="modal fade mt-5" id="editClaimModal" tabindex="-1">
      <div class="modal-dialog">
        <div class="modal-content bg-dark text-light">
          <div class="modal-header border-bottom border-secondary">
            <h5 class="modal-title">Editar Estado del Reclamo #{{ selectedClaim?.id }}</h5>
            <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body" v-if="selectedClaim">
            <div class="mb-3">
              <label class="form-label">Estado del Reclamo</label>
              <select class="form-select" v-model="selectedClaim.status">
                <option value="pending">Pendiente</option>
                <option value="reviewing">En Revisión</option>
                <option value="approved">Aprobado</option>
                <option value="rejected">Rechazado</option>
                <option value="resolved">Resuelto</option>
              </select>
            </div>
            <div class="mb-3">
              <label class="form-label">Nota Administrativa (Solo visible para el admin)</label>
              <textarea 
                class="form-control" 
                v-model="selectedClaim.admin_notes"
                rows="3"
                placeholder="Agregar notas internas sobre el reclamo..."
              ></textarea>
            </div>
            
            <div class="mb-3" v-if="['resolved', 'approved'].includes(selectedClaim.status)">
              <label class="form-label">Detalles de Resolución (Visible para el cliente)</label>
              <textarea 
                class="form-control" 
                v-model="selectedClaim.resolution_details"
                rows="3"
                placeholder="Describir cómo se resolvió el reclamo o qué compensación se ofreció..."
              ></textarea>
            </div>
          </div>
          <div class="modal-footer border-top border-secondary">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancelar</button>
            <button type="button" class="btn btn-primary" @click="updateClaim" :disabled="isUpdating">
              <span v-if="isUpdating">
                <i class="fas fa-spinner fa-spin me-2"></i>Actualizando...
              </span>
              <span v-else>
                <i class="fas fa-save me-2"></i>Guardar Cambios
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Photo Modal -->
    <div v-if="showPhotoModal" class="photo-modal" @click="closePhotoModal">
      <div class="photo-modal-content" @click.stop>
        <button class="close-button" @click="closePhotoModal">&times;</button>
        <img :src="currentPhoto" alt="Foto ampliada" />
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useToast } from 'vue-toastification'
import { updateWarrantyClaim } from '@/services/warranty'
import router from '@/router'
import apiClient from '@/services/apiClient'

const toast = useToast()
const bootstrap = window.bootstrap

// Estado
const claims = ref([])
const isLoading = ref(true)
const error = ref(null)
const searchQuery = ref('')
const selectedClaim = ref(null)
const isUpdating = ref(false)
const showPhotoModal = ref(false)
const currentPhoto = ref('')

// Fetch warranty claims with pagination support
const fetchClaims = async () => {
  try {
    isLoading.value = true;
    error.value = null;

    const token = localStorage.getItem('accessToken');
    if (!token) {
      throw new Error('No hay sesión activa');
    }

    const response = await apiClient.get("/warranty-claims/", {
      headers: {
        'Authorization': `Bearer ${token}`
      },
      params: {
        include_user_details: true
      }
    });

    console.log('API response:', response.data);

    // Handle paginated response structure
    if (response.data && response.data.results) {
      claims.value = response.data.results;
    } else if (Array.isArray(response.data)) {
      claims.value = response.data;
    } else {
      claims.value = [];
    }
    
    console.log('Processed claims:', claims.value);
    
  } catch (err) {
    error.value = err.message || 'Error al cargar los reclamos';
    console.error('Error fetching claims:', err);
    toast.error('Error al cargar los reclamos');
  } finally {
    isLoading.value = false;
  }
}

// Computed
const totalClaims = computed(() => claims.value.length)

const filteredClaims = computed(() => {
  if (!searchQuery.value) return claims.value
  
  const query = searchQuery.value.toLowerCase()
  return claims.value.filter(claim => 
    claim.name.toLowerCase().includes(query) || 
    claim.email.toLowerCase().includes(query) || 
    claim.order_number?.toLowerCase().includes(query) ||
    claim.product_name?.toLowerCase().includes(query)
  )
})

// Function to handle search input with debounce
const debounceSearch = (() => {
  let timeout = null;
  return () => {
    clearTimeout(timeout);
    timeout = setTimeout(() => {
      // The search happens automatically through the computed property
    }, 300);
  };
})();

// Methods
const forceRefresh = async () => {
  try {
    await fetchClaims()
    toast.success('Reclamos actualizados')
  } catch (err) {
    toast.error('Error al actualizar los reclamos')
  }
}

// Status helpers with safe fallbacks
const getStatusLabel = (status) => {
  const statusMap = {
    pending: 'Pendiente de revisión',
    reviewing: 'En revisión',
    approved: 'Aprobado',
    rejected: 'Rechazado',
    resolved: 'Resuelto'
  };
  return statusMap[status] || status || 'Desconocido';
}

const getStatusIcon = (status) => {
  const iconMap = {
    pending: 'fas fa-clock',
    reviewing: 'fas fa-search',
    approved: 'fas fa-check',
    rejected: 'fas fa-times',
    resolved: 'fas fa-check-double'
  };
  return iconMap[status] || 'fas fa-question-circle';
}

const getStatusClass = (status) => {
  const classMap = {
    pending: 'bg-warning',
    reviewing: 'bg-info',
    approved: 'bg-success',
    rejected: 'bg-danger',
    resolved: 'bg-primary'
  };
  return classMap[status] || 'bg-secondary';
}

const viewClaimDetails = (claim) => {
  selectedClaim.value = { ...claim }
  const modal = new bootstrap.Modal(document.getElementById('claimDetailsModal'))
  modal.show()
}

// Actualizar el método editClaim para hacer una copia profunda
const editClaim = (claim) => {
  selectedClaim.value = JSON.parse(JSON.stringify({
    ...claim,
    resolution_details: claim.resolution_details || ''
  }));
  const modal = new bootstrap.Modal(document.getElementById('editClaimModal'));
  modal.show();
}

const updateClaim = async () => {
  try {
    isUpdating.value = true;
    console.log('Updating claim:', selectedClaim.value); // Debug log

    const updateData = {
      status: selectedClaim.value.status,
      admin_notes: selectedClaim.value.admin_notes || '',
      resolution_details: selectedClaim.value.resolution_details || ''
    };

    console.log('Update data:', updateData); // Debug log

    // Make the API call
    const updatedClaim = await updateWarrantyClaim(selectedClaim.value.id, updateData);
    console.log('Updated claim response:', updatedClaim); // Debug log

    // Update the claim in the local list
    const index = claims.value.findIndex(c => c.id === selectedClaim.value.id);
    if (index !== -1) {
      claims.value[index] = { ...claims.value[index], ...updatedClaim };
      claims.value = [...claims.value]; // Force reactivity update
    }

    // Close modal
    const modal = bootstrap.Modal.getInstance(document.getElementById('editClaimModal'));
    if (modal) {
      modal.hide();
    }

    // Show success message and refresh
    toast.success('Reclamo actualizado exitosamente');
    await fetchClaims();

  } catch (error) {
    console.error('Error updating claim:', error);
    toast.error(error.message || 'Error al actualizar el reclamo');
  } finally {
    isUpdating.value = false;
  }
}

const viewOrderDetails = (orderNumber) => {
  router.push(`/admin/pedidos?orderId=${orderNumber}`)
}

const viewUserDetails = async (claim) => {
  try {
    // Si tenemos el user_id directamente del claim, usarlo
    if (claim.user_id) {
      router.push(`/admin/usuarios/${claim.user_id}`);
      return;
    }
    
    // Si tenemos el customer en la orden asociada, usarlo
    if (claim.order && claim.order.customer_id) {
      router.push(`/admin/usuarios/${claim.order.customer_id}`);
      return;
    }
    
    // Si solo tenemos email, intentar buscar el usuario por email
    const userEmail = claim.email;
    if (!userEmail) {
      toast.warning('No se encontró información del usuario');
      return;
    }
    
    // Antes de navegar, intentamos obtener el ID del usuario mediante su email
    const token = localStorage.getItem('accessToken');
    if (!token) {
      throw new Error('No hay sesión activa');
    }
    
    // Hacer consulta a la API para buscar el usuario por email
    const response = await apiClient.get(`/users/?email=${encodeURIComponent(userEmail)}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    // Obtener el ID del usuario si existe
    let userId;
    if (response.data && response.data.length > 0) {
      userId = response.data[0].id;
    } else if (response.data && response.data.results && response.data.results.length > 0) {
      userId = response.data.results[0].id;
    }
    
    // Si tenemos el ID, navegar directo a la página de detalles
    if (userId) {
      router.push(`/admin/usuarios/${userId}`);
    } else {
      // Si no encontramos el ID, seguimos usando el método antiguo con email como parámetro
      router.push(`/admin/usuarios?email=${encodeURIComponent(userEmail)}`);
    }
  } catch (err) {
    console.error('Error al buscar usuario por email:', err);
    // En caso de error, usamos email como parámetro
    const userEmail = claim.email;
    router.push(`/admin/usuarios?email=${encodeURIComponent(userEmail)}`);
  }
}

const formatDate = (date) => {
  return new Date(date).toLocaleString('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const formatSimpleDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('es-ES', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
}

const openPhotoModal = (photo) => {
  currentPhoto.value = photo
  showPhotoModal.value = true
}

const closePhotoModal = () => {
  showPhotoModal.value = false
  currentPhoto.value = ''
}

// Lifecycle
onMounted(() => {
  fetchClaims();
  
  // Refresh claims every 2 minutes
  const refreshInterval = setInterval(() => {
    fetchClaims();
  }, 2 * 60 * 1000);

  onUnmounted(() => {
    clearInterval(refreshInterval);
  });
})
</script>

<style scoped>
.warranty-claims-section {
  min-height: 100vh;
  background: var(--black-color);
}

.info-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
}

.info-row i {
  width: 18px;
  text-align: center;
}

.photos-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 1rem;
  margin-top: 1rem;
}

.photo-item {
  aspect-ratio: 1;
  overflow: hidden;
  border-radius: 8px;
  cursor: pointer;
  position: relative;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.photo-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.photo-item:hover img {
  transform: scale(1.1);
}

.photo-label {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 4px;
  text-align: center;
  font-size: 0.8rem;
}

.section-card {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1rem;
}

.section-title {
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  padding-bottom: 0.5rem;
  margin-bottom: 1rem;
  font-weight: 600;
  color: var(--primary-color);
}

.info-grid {
  display: grid;
  gap: 0.75rem;
}

.info-item {
  display: grid;
  grid-template-columns: 120px 1fr;
  gap: 1rem;
  align-items: flex-start;
}

.info-item .label {
  font-weight: 500;
  color: rgba(255, 255, 255, 0.7);
}

.photo-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
}

.photo-modal-content {
  max-width: 90%;
  max-height: 90vh;
  position: relative;
}

.photo-modal-content img {
  max-width: 100%;
  max-height: 90vh;
  object-fit: contain;
  display: block;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.5);
}

.close-button {
  position: absolute;
  top: -40px;
  right: 0;
  background: none;
  border: none;
  color: white;
  font-size: 2rem;
  cursor: pointer;
}

.cursor-pointer {
  cursor: pointer;
}

.text-primary:hover {
  text-decoration: underline;
}

.skeleton-loader {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  padding: 1rem;
  height: 200px;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.skeleton-text {
  height: 1rem;
  background: linear-gradient(90deg, #333 25%, #444 50%, #333 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 4px;
}

@keyframes loading {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

@media (max-width: 768px) {
  .info-item {
    grid-template-columns: 1fr;
  }
  
  .photos-grid {
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  }
}
</style>
